import React, { useState, useEffect, useCallback } from 'react';
import { FiPlus, FiEdit, FiTrash2, FiX } from 'react-icons/fi';
import useApi from '../../Hooks/useApi';
import useLanguage from '../../Hooks/useLanguage';
import './css/todayNotifications.css';

const TodayNotifications = ({ isVisible, onClose }) => {
  const api = useApi();
  const [todayNotifications, setTodayNotifications] = useState([]);
  const [seenNotifications, setSeenNotifications] = useState(new Set());

  // Get language and translations
  const lang = useLanguage();
  const translations = {
    nl: {
      title: "Vandaag's Wijzigingen",
      breakfast: "Ontbijt",
      lunch: "Lunch", 
      dinner: "Diner",
      noChanges: "Geen wijzigingen vandaag",
      eventPrefixes: {
        creation: "Nieuw",
        update: "Update", 
        deletion: "Verwijderd"
      },
      guests: "personen",
      guest: "persoon",
      at: "om",
      by: "-"
    },
    en: {
      title: "Today's Changes",
      breakfast: "Breakfast",
      lunch: "Lunch",
      dinner: "Dinner", 
      noChanges: "No changes today",
      eventPrefixes: {
        creation: "New",
        update: "Updated",
        deletion: "Deleted"
      },
      guests: "guests",
      guest: "guest",
      at: "at",
      by: "-"
    },
    fr: {
      title: "Changements d'Aujourd'hui",
      breakfast: "Petit-déjeuner",
      lunch: "Déjeuner",
      dinner: "Dîner",
      noChanges: "Aucun changement aujourd'hui", 
      eventPrefixes: {
        creation: "Nouveau",
        update: "Mis à jour",
        deletion: "Supprimé"
      },
      guests: "personnes",
      guest: "personne",
      at: "à",
      by: "-"
    }
  };
  
  const t = translations[lang];

  // Helper function to get correct singular/plural form
  const getGuestText = (count) => {
    return count === 1 ? t.guest : t.guests;
  };

  // Check if a date is today
  const isToday = (dateString) => {
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0]; // YYYY-MM-DD format
    return dateString === todayStr;
  };

  // Check if timestamp is today
  const isTimestampToday = (timestamp) => {
    const today = new Date();
    const timestampDate = new Date(timestamp);
    return today.toDateString() === timestampDate.toDateString();
  };

  // Check if a notification should be shown (not in the past)
  const shouldShowNotification = (notification) => {
    if (!notification.date || !notification.time) return true; // Show if no date or time
    
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];
    
    // If not today, show it
    if (notification.date !== todayStr) return true;
    
    // If today, check the time
    const [hours, minutes] = notification.time.split(':').map(Number);
    const notificationTime = new Date();
    notificationTime.setHours(hours, minutes, 0, 0);
    
    // Only show if notification time is in the future or within the last 5 minutes
    const fiveMinutesAgo = new Date(today.getTime() - 5 * 60 * 1000);
    return notificationTime > fiveMinutesAgo;
  };

  // Get time period based on time string
  const getTimePeriod = (timeStr) => {
    if (!timeStr) return 'dinner'; // Default to dinner if no time
    
    const [hours] = timeStr.split(':').map(Number);
    
    if (hours >= 7 && hours < 11) return 'breakfast';
    if (hours >= 11 && hours < 16) return 'lunch';
    return 'dinner';
  };

  // Get icon for event type
  const getEventIcon = (event) => {
    switch (event) {
      case 'creation': return <FiPlus className="event-icon creation" />;
      case 'update': return <FiEdit className="event-icon update" />;
      case 'deletion': return <FiTrash2 className="event-icon deletion" />;
      default: return <FiPlus className="event-icon default" />;
    }
  };

  // Load notifications from cache and filter for today
  const loadTodayNotifications = useCallback(() => {
    try {
      const cached = localStorage.getItem('notificationsCache');
      if (!cached) return;
      
      const notifications = JSON.parse(cached);
      const seen = JSON.parse(localStorage.getItem('seenTodayNotifications') || '[]');
      setSeenNotifications(new Set(seen));
      
      // Filter for today's notifications (exclude giftcard_purchase and past times)
      const todayItems = notifications.filter(notification => {
        if (notification.event === 'giftcard_purchase') return false;
        
        const hasDateToday = notification.date && isToday(notification.date);
        const hasTimestampToday = notification.creationTimestamp && isTimestampToday(notification.creationTimestamp);
        const isNotPast = shouldShowNotification(notification);
        
        return hasDateToday && hasTimestampToday && isNotPast;
      });

      setTodayNotifications(todayItems);
    } catch (error) {
      console.error('Error loading today notifications:', error);
    }
  }, []);

  // Load notifications on mount and when visibility changes
  useEffect(() => {
    if (isVisible) {
      loadTodayNotifications();
    }
  }, [isVisible, loadTodayNotifications]);

  // Listen for notifications cache updates
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'notificationsCache') {
        loadTodayNotifications();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [loadTodayNotifications]);

  // Handle closing and marking as seen
  const handleClose = async () => {
    try {
      // Get unseen notification IDs
      const unseenIds = todayNotifications
        .filter(n => !seenNotifications.has(n._id || n.reservationId))
        .map(n => n._id || n.reservationId);

      if (unseenIds.length > 0) {
        // Mark as seen in localStorage
        const newSeen = [...seenNotifications, ...unseenIds];
        localStorage.setItem('seenTodayNotifications', JSON.stringify(newSeen));
        
        // Optionally mark as read on server (using existing API endpoint)
        try {
          await api.patch(`${window.baseDomain}api/notifications`, {
            notificationIds: unseenIds
          });
        } catch (err) {
          console.error('Error marking notifications as read:', err);
        }
      }
    } catch (error) {
      console.error('Error handling close:', error);
    }
    
    onClose();
  };

  // Group notifications by time period
  const groupedNotifications = todayNotifications.reduce((groups, notification) => {
    const period = getTimePeriod(notification.time);
    if (!groups[period]) groups[period] = [];
    groups[period].push(notification);
    return groups;
  }, {});

  // Sort notifications within each group by time
  Object.keys(groupedNotifications).forEach(period => {
    groupedNotifications[period].sort((a, b) => {
      const timeA = a.time || '00:00';
      const timeB = b.time || '00:00';
      return timeA.localeCompare(timeB);
    });
  });

  // Check if there are new (unseen) notifications
  const hasNewNotifications = todayNotifications.some(n => 
    !seenNotifications.has(n._id || n.reservationId)
  );

  if (!isVisible || todayNotifications.length === 0) return null;

  return (
    <div className="today-notifications-component">
      <div className="today-notifications-container">
        <div className="today-notifications-header">
          <h3 className="today-notifications-title">
            {t.title}
            {hasNewNotifications && <span className="new-indicator">•</span>}
          </h3>
          <button className="today-notifications-close" onClick={handleClose}>
            <FiX />
          </button>
        </div>
        
        <div className="today-notifications-content">
          {['breakfast', 'lunch', 'dinner'].map(period => {
            const notifications = groupedNotifications[period];
            if (!notifications || notifications.length === 0) return null;
            
            return (
              <div key={period} className="time-period-section">
                <h4 className="time-period-title">{t[period]}</h4>
                <ul className="notifications-list">
                  {notifications.map(notification => {
                    const id = notification._id || notification.reservationId;
                    const isNew = !seenNotifications.has(id);
                    
                    return (
                      <li key={id} className={`notification-item ${isNew ? 'new' : ''}`}>
                        {getEventIcon(notification.event)}
                        <span className="notification-text">
                          <strong>{t.eventPrefixes[notification.event] || 'Update'}</strong>
                          {notification.event === 'update' && notification.oldGuests && notification.newGuests ? (
                            <span> - {notification.oldGuests} → {notification.newGuests} {getGuestText(notification.newGuests)}</span>
                          ) : notification.guests ? (
                            <span> - {notification.guests} {getGuestText(notification.guests)}</span>
                          ) : null}
                          {notification.time && (
                            <span> {t.at} {notification.time}</span>
                          )}
                          {(notification.firstName || notification.lastName) && (
                            <span> {t.by} {notification.firstName} {notification.lastName}</span>
                          )}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TodayNotifications;